import { captureMessage } from '@sentry/react';
import { addJourneyStep } from './journey';
const CHECK_INTERVAL_MS = 15000;
// проблема должна держаться столько проверок подряд, чтобы не ловить переходные состояния
const CHECKS_TO_REPORT = 2;
const PROBLEM_TITLES = {
    'no-remote-tracks': 'Media health: remote sources signaled, but no remote tracks',
    'no-media-received': 'Media health: remote tracks exist, but nothing is received',
    'no-media-sent': 'Media health: local tracks unmuted, but nothing is sent'
};
let timer;
let streaks = {};
let reported = new Set();
/**
 * Снимок состояния медиа: что ждём от сервера, что реально получили и что идёт по сети.
 *
 * @param {IReduxState} state - Redux state.
 * @returns {Object|undefined}
 */
const collectSnapshot = (state) => {
    const conference = state['features/base/conference'].conference;
    if (!conference) {
        return undefined;
    }
    const participants = conference.getParticipants().filter((p) => !p.isHidden?.());
    const signaled = { audio: 0,
        video: 0 };
    participants.forEach((p) => {
        p.getSources?.()?.forEach((sources, mediaType) => {
            sources.forEach(({ muted }) => {
                if (!muted && mediaType in signaled) {
                    signaled[mediaType]++;
                }
            });
        });
    });
    const tracks = state['features/base/tracks'];
    const countTracks = (local, onlyUnmuted) => tracks
        .filter(t => t.local === local && t.jitsiTrack && (!onlyUnmuted || !t.muted))
        .reduce((acc, t) => {
        acc[t.mediaType] = (acc[t.mediaType] ?? 0) + 1;
        return acc;
    }, {});
    let stats;
    try {
        stats = APP.conference.getStats();
    }
    catch (e) {
        stats = undefined;
    }
    return {
        participants: participants.length,
        signaledUnmuted: signaled,
        remoteTracks: countTracks(false, false),
        remoteUnmutedTracks: countTracks(false, true),
        localUnmutedTracks: countTracks(true, true),
        bitrate: stats?.bitrate,
        packetLoss: stats?.packetLoss,
        connectionQuality: stats?.connectionQuality,
        // ip не берём, только типы кандидатов и протокол
        transport: stats?.transport?.map((t) => {
            return {
                type: t.type,
                p2p: t.p2p,
                localCandidateType: t.localCandidateType,
                remoteCandidateType: t.remoteCandidateType,
                networkType: t.networkType,
                rtt: t.rtt
            };
        }),
        p2p: conference.isP2PActive?.(),
        iceState: conference.getConnectionState?.(),
        interrupted: conference.isConnectionInterrupted?.()
    };
};
const sum = (counts) => Object.values(counts).reduce((a, b) => a + b, 0);
/**
 * Определяет проблемы по снимку.
 *
 * @param {Object} snapshot - Снимок из collectSnapshot.
 * @returns {MediaProblem[]}
 */
const detectProblems = (snapshot) => {
    const problems = [];
    // прерывание соединения репортится отдельно
    if (!snapshot.participants || snapshot.interrupted) {
        return problems;
    }
    const signaledUnmuted = snapshot.signaledUnmuted.audio + snapshot.signaledUnmuted.video;
    // сервер сказал, что у собеседников есть включенные источники, а треков нет — проблема сигналинга
    if (signaledUnmuted > 0 && sum(snapshot.remoteTracks) === 0) {
        problems.push('no-remote-tracks');
    }
    // треки есть, но байты не идут — проблема транспорта (ice/dtls/bridge)
    if (sum(snapshot.remoteUnmutedTracks) > 0 && snapshot.bitrate?.download === 0) {
        problems.push('no-media-received');
    }
    if (sum(snapshot.localUnmutedTracks) > 0 && snapshot.bitrate?.upload === 0) {
        problems.push('no-media-sent');
    }
    return problems;
};
/**
 * Одна проверка.
 *
 * @param {Function} getState - Redux getState.
 * @returns {void}
 */
const check = (getState) => {
    const snapshot = collectSnapshot(getState());
    if (!snapshot) {
        return;
    }
    const problems = detectProblems(snapshot);
    Object.keys(streaks).forEach(problem => {
        if (!problems.includes(problem)) {
            streaks[problem] = 0;
        }
    });
    problems.forEach(problem => {
        streaks[problem] = (streaks[problem] ?? 0) + 1;
        if (streaks[problem] < CHECKS_TO_REPORT || reported.has(problem)) {
            return;
        }
        reported.add(problem);
        addJourneyStep(`media problem: ${problem}`, undefined, 'warning');
        captureMessage(PROBLEM_TITLES[problem], {
            level: 'warning',
            fingerprint: ['media-health', problem],
            tags: {
                'failure.kind': 'media',
                'failure.reason': problem,
                'media.p2p': String(snapshot.p2p),
                'media.ice_state': String(snapshot.iceState)
            },
            extra: { snapshot }
        });
    });
};
/**
 * Останавливает проверку.
 *
 * @returns {void}
 */
export const stopMediaHealthCheck = () => {
    window.clearInterval(timer);
    timer = undefined;
    streaks = {};
    reported = new Set();
};
/**
 * Запускает периодическую проверку медиа после входа в конференцию.
 *
 * @param {Function} getState - Redux getState.
 * @returns {void}
 */
export const startMediaHealthCheck = (getState) => {
    stopMediaHealthCheck();
    timer = window.setInterval(() => {
        try {
            check(getState);
        }
        catch (e) {
            // проверка не должна ломать приложение
        }
    }, CHECK_INTERVAL_MS);
};
